import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { LocateFixed, Loader2 } from 'lucide-react';

/**
 * "Locate me" for every map (Jim 2026-10-13: "would be good to have a locate me button
 * for all maps"). Sits in the map's top-right corner; one tap centres the map on the
 * device and drops a blue dot with its accuracy circle. Rendered inside a MapContainer.
 */
export function LocateMeControl({ zoom = 15 }: { zoom?: number }) {
  const map = useMap();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    return () => {
      layer.current?.remove();
    };
  }, [map]);

  const locate = () => {
    if (!navigator.geolocation) {
      setProblem('This browser cannot share your location.');
      return;
    }
    setBusy(true);
    setProblem(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setBusy(false);
        const here = L.latLng(pos.coords.latitude, pos.coords.longitude);
        layer.current?.remove();
        layer.current = L.layerGroup([
          L.circle(here, {
            radius: Math.max(pos.coords.accuracy, 5),
            color: '#2563eb',
            weight: 1,
            fillOpacity: 0.12,
            interactive: false,
          }),
          L.circleMarker(here, {
            radius: 7,
            color: '#ffffff',
            weight: 2,
            fillColor: '#2563eb',
            fillOpacity: 1,
            interactive: false,
          }),
        ]).addTo(map);
        map.flyTo(here, Math.max(map.getZoom(), zoom), { duration: 0.6 });
      },
      (err) => {
        setBusy(false);
        setProblem(
          err.code === err.PERMISSION_DENIED
            ? 'Location is turned off for this site. Allow it in the browser settings.'
            : 'Could not find your location. Try again.'
        );
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  };

  return (
    <div
      className="leaflet-top leaflet-right"
      style={{ pointerEvents: 'none' }}
    >
      <div
        className="leaflet-control flex flex-col items-end gap-1"
        style={{ pointerEvents: 'auto' }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            locate();
          }}
          aria-label="Locate me"
          title="Locate me"
          className="w-9 h-9 rounded-lg bg-white text-slate-700 shadow border border-slate-300 flex items-center justify-center hover:bg-slate-50"
        >
          {busy ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <LocateFixed className="w-4 h-4 text-blue-600" />
          )}
        </button>
        {problem && (
          <div
            role="status"
            className="max-w-[14rem] rounded-md bg-white/95 px-2 py-1 text-xs text-slate-700 shadow"
          >
            {problem}
          </div>
        )}
      </div>
    </div>
  );
}
